import { Navigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useAuth } from 'react-oidc-context';
import { COMPANY_TYPE } from '@constants/app';
import withChatbotLayout from '@product-framework/components/Chatbot/withChatbotLayout';
import NotificationStreamConnection from '@product-framework/pages/Notifications/components/NotificationStreamConnection';
import RouteRBACCheck from './RouteRBACCheck';
import AppDataProvider from '../../providers/AppDataProvider';
import { useSnackbar } from '../../providers/SnackbarProvider';
import LoadingEvent from '../UI/Skeleton/LoadingEvent';
import SnackbarApiErrorMessage from '../UI/SnackbarApiErrorMessage';
import { useDataProvider } from '../../providers/DataProvider';

export function ProtectedPage({
  componentProps = {},
  Component,
  AuthorizationWrapper = null,
  AuthorizationWrapperProps = {},
}) {
  const page = <Component {...componentProps} />;
  return AuthorizationWrapper ? (
    <AuthorizationWrapper {...AuthorizationWrapperProps}>
      {page}
    </AuthorizationWrapper>
  ) : (
    page
  );
}

function ProtectedRoute({
  componentProps = {},
  Component,
  ErrorPage,
  ERROR_PAGE_TYPE,
  AuthorizationWrapper = null,
  AuthorizationWrapperProps = {},
}) {
  const {
    isLoading = false,
    error: authError,
    isAuthenticated,
  } = useAuth() || {
    isLoading: false,
    isAuthenticated: false,
    error: 'Auth Error',
  };
  const snackbar = useSnackbar();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl } = useDataProvider(provider);

  const getInjectableAdditionalMetaData = async userAccess => {
    const getCountryByCode = async countryCode => {
      const url = `${getApiUrl()}/sxcl/v1/countries?countryCode=${countryCode}&limit=1`;
      const response = await get(url);
      if (response?.ok) {
        return {
          ...response?.data?.data?.[0],
          label: response?.data?.data?.[0]?.countryName,
          value: response?.data?.data?.[0]?.id,
        };
      }
      return null;
    };

    const fetchSP = async spId => {
      const url = `${getApiUrl()}/sxcl/v1/service-providers/${spId}`;
      const response = await get(url);
      if (response?.ok) {
        return response.data;
      }
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
      return null;
    };

    const getUserProfile = async () => {
      let userProfile = {};
      if (userAccess?.company?.companyType === COMPANY_TYPE.SERVICE_PROVIDER) {
        const spDetails = await fetchSP(userAccess.company.id);
        if (spDetails) {
          userProfile = spDetails;
        }
      }
      return userProfile;
    };

    const countryUS = await getCountryByCode('US');
    const userProfile = await getUserProfile();

    return {
      staticCountry: countryUS,
      loggedInUser: userProfile,
    };
  };

  if (authError) {
    if (isAuthenticated) {
      return (
        <ErrorPage
          redirectUri={window.location.href}
          type={ERROR_PAGE_TYPE.AUTH_ERROR}
        />
      );
    }

    const search = new URLSearchParams({
      redirect_url: window.location.href,
    }).toString();

    return (
      <Navigate
        replace
        to={{
          pathname: '/',
          search: `?${search}`,
        }}
      />
    );
  }

  if (isAuthenticated) {
    return (
      <RouteRBACCheck>
        <NotificationStreamConnection />
        <AppDataProvider
          getInjectableAdditionalMetaData={getInjectableAdditionalMetaData}
          metaDataUrl="/sxcl/v1/config">
          <ProtectedPage
            Component={Component}
            componentProps={componentProps}
            AuthorizationWrapper={AuthorizationWrapper}
            AuthorizationWrapperProps={AuthorizationWrapperProps}
          />
        </AppDataProvider>
      </RouteRBACCheck>
    );
  }

  if (isLoading) {
    return (
      <LoadingEvent>
        <FormattedMessage
          id="AUTHENTICATING"
          defaultMessage="Authenticating..."
        />
      </LoadingEvent>
    );
  }

  return (
    <Navigate
      state={{ redirect_uri: window.location.href }}
      to={{
        pathname: '/unauthorized',
      }}
    />
  );
}

export default withChatbotLayout(ProtectedRoute);
